import { Workbook, Worksheet, type Row } from "exceljs";
import type { CellValue } from "exceljs";
import type { InspectionItem, InspectionsType } from "@app/types";

const WORKSHEET_NAMES = ["InspectionForm", "Inspection Form", "Inspections"];
const ANSWER_OPTIONS_SHEET_NAME = "AnswerOptionsData";
const HEADER_ROW_INDEX = 1;
const START_ROW_INDEX = 2;
const MAX_TEMPLATE_ROWS = 100;
export const MAX_INSPECTION_FORM_ITEMS = MAX_TEMPLATE_ROWS;
const ITEM_NAME_COLUMN = 1;
const DESCRIPTION_COLUMN = 2;
const ANSWER_OPTION_COLUMN = 3;
const FIRST_PREDEFINED_OPTION_COLUMN = 4;
export const PREDEFINED_OPTION_COLUMN_COUNT = 10;
const MAX_PREDEFINED_OPTION_NAME_LENGTH = 100;
const PROTECTION_PASSWORD = "2286";

type InspectionFormItemLike = {
  resultType?: string | null;
  itemCode?: string | null;
};

export function isInspectionFormMeterItem(
  item: InspectionFormItemLike,
): boolean {
  return item.resultType === "NUMERIC" && !!item.itemCode;
}

export function limitInspectionFormItems<T extends InspectionFormItemLike>(
  items: T[],
  maxCustomItems: number = MAX_INSPECTION_FORM_ITEMS,
): T[] {
  const meterItems: T[] = [];
  const customItems: T[] = [];

  for (const item of items) {
    if (isInspectionFormMeterItem(item)) {
      meterItems.push(item);
    } else {
      customItems.push(item);
    }
  }

  return [...meterItems, ...customItems.slice(0, maxCustomItems)];
}

export const INSPECTION_BULK_UPLOAD_ERROR = {
  INVALID_TEMPLATE_FORMAT: "INVALID_TEMPLATE_FORMAT",
  NO_INFORMATION: "NO_INFORMATION",
  NO_VALID_DATA: "NO_VALID_DATA",
} as const;

// Passed in from the component via t()
export type InspectionTemplateTranslations = {
  headers: {
    itemName: string;
    description: string;
    answerOption: string;
    predefinedOptionColumns: string[];
  };
  answerOptions: {
    okOrAnomary: string; // "Abnormal, Normal"
    okAnomaryNotApplicable: string; // "N/A, Abnormal, Normal"
    commentFile: string; // "None"
    predefinedComment: string; // "Predefined comment selection"
  };
  meterItems: {
    serviceMeter: string;
    odometer: string;
  };
};

export const buildInspectionTemplateOptionColumnHeaders = (
  translate: (key: string, options?: { number: number }) => string,
) =>
  Array.from({ length: PREDEFINED_OPTION_COLUMN_COUNT }, (_, index) =>
    translate("bulkTemplateOptionColumn", { number: index + 1 }),
  );

const getPredefinedOptionColumnHeaders = (
  translations: InspectionTemplateTranslations,
) => translations.headers.predefinedOptionColumns;

const getPredefinedOptionColumnIndex = (optionIndex: number) =>
  FIRST_PREDEFINED_OPTION_COLUMN + optionIndex;

// Built from translations at call time
const buildAnswerOptionMaps = (
  translations: InspectionTemplateTranslations,
) => {
  const {
    okOrAnomary,
    okAnomaryNotApplicable,
    commentFile,
    predefinedComment,
  } = translations.answerOptions;

  const answerOptionByResultType: Record<string, string> = {
    OK_OR_ANOMARY: okOrAnomary,
    OK_ANOMARY_NOT_APPLICABLE: okAnomaryNotApplicable,
    NONE: commentFile,
    FREE_TEXT: predefinedComment,
  };

  const resultTypeByAnswerOption: Record<string, string> = {
    [okOrAnomary.toLowerCase()]: "OK_OR_ANOMARY",
    [okAnomaryNotApplicable.toLowerCase()]: "OK_ANOMARY_NOT_APPLICABLE",
    [commentFile.toLowerCase()]: "NONE",
    [predefinedComment.toLowerCase()]: "FREE_TEXT",
  };

  return { answerOptionByResultType, resultTypeByAnswerOption };
};

type InspectionTemplateItem = {
  name?: string;
  description?: string;
  resultType?: string;
  position?: number;
  options?: {
    name: string;
    position?: number;
  }[];
};

export type InspectionBulkTemplateData = {
  inspectionItems?: InspectionTemplateItem[];
  customInspectionItems?: InspectionTemplateItem[];
  items?: InspectionTemplateItem[];
};

const getTargetWorksheet = (workbook: Workbook): Worksheet | undefined => {
  for (const worksheetName of WORKSHEET_NAMES) {
    const worksheet = workbook.getWorksheet(worksheetName);
    if (worksheet) return worksheet;
  }
  return workbook.worksheets[0];
};

const quoteWorksheetName = (worksheetName: string) =>
  `'${worksheetName.replace(/'/g, "''")}'`;

const getTemplateItems = (
  templateData: InspectionBulkTemplateData,
  answerOptionByResultType: Record<string, string>,
): InspectionTemplateItem[] => {
  const items =
    templateData.inspectionItems ??
    templateData.customInspectionItems ??
    templateData.items ??
    [];

  return items
    .filter((item) => !!answerOptionByResultType[item.resultType ?? ""])
    .sort((itemA, itemB) => (itemA.position ?? 0) - (itemB.position ?? 0))
    .slice(0, MAX_INSPECTION_FORM_ITEMS);
};

const getCellText = (value: CellValue): string => {
  if (value === null || value === undefined) return "";
  if (value instanceof Date) return value.toISOString();
  if (typeof value === "object") {
    if ("text" in value && typeof value.text === "string")
      return value.text.trim();
    if ("result" in value) return getCellText(value.result as CellValue);
    if ("richText" in value && Array.isArray(value.richText)) {
      return value.richText
        .map((item) => item.text)
        .join("")
        .trim();
    }
  }
  return String(value).trim();
};

const throwUploadError = (
  type: (typeof INSPECTION_BULK_UPLOAD_ERROR)[keyof typeof INSPECTION_BULK_UPLOAD_ERROR],
) => {
  throw new Error(type);
};

const validateUploadHeaders = (
  worksheet: Worksheet,
  translations: InspectionTemplateTranslations,
) => {
  const headerRow = worksheet.getRow(HEADER_ROW_INDEX);
  const optionHeaders = getPredefinedOptionColumnHeaders(translations);
  const excelHeaders = [
    getCellText(headerRow.getCell(ITEM_NAME_COLUMN).value).toLowerCase(),
    getCellText(headerRow.getCell(DESCRIPTION_COLUMN).value).toLowerCase(),
    getCellText(headerRow.getCell(ANSWER_OPTION_COLUMN).value).toLowerCase(),
    ...optionHeaders.map((header, index) =>
      getCellText(
        headerRow.getCell(getPredefinedOptionColumnIndex(index)).value,
      ).toLowerCase(),
    ),
  ];

  const expectedHeaders = [
    translations.headers.itemName.toLowerCase(),
    translations.headers.description.toLowerCase(),
    translations.headers.answerOption.toLowerCase(),
    ...optionHeaders.map((header) => header.toLowerCase()),
  ];

  const isValid = expectedHeaders.every(
    (header, index) => excelHeaders[index] === header,
  );

  if (!isValid) {
    throwUploadError(INSPECTION_BULK_UPLOAD_ERROR.INVALID_TEMPLATE_FORMAT);
  }
};

const ensureAnswerOptionsSheet = (
  workbook: Workbook,
  translations: InspectionTemplateTranslations,
) => {
  const worksheet =
    workbook.getWorksheet(ANSWER_OPTIONS_SHEET_NAME) ??
    workbook.addWorksheet(ANSWER_OPTIONS_SHEET_NAME);

  const {
    okOrAnomary,
    okAnomaryNotApplicable,
    commentFile,
    predefinedComment,
  } = translations.answerOptions;
  [okOrAnomary, okAnomaryNotApplicable, commentFile, predefinedComment].forEach(
    (option, index) => {
      worksheet.getCell(index + 1, 1).value = option;
    },
  );

  worksheet.state = "veryHidden";
  return worksheet;
};

const applyWorksheetRules = async (
  workbook: Workbook,
  worksheet: Worksheet,
  translations: InspectionTemplateTranslations,
) => {
  const answerOptionsWorksheet = ensureAnswerOptionsSheet(
    workbook,
    translations,
  );
  const answerOptionFormula = `=${quoteWorksheetName(
    answerOptionsWorksheet.name,
  )}!$A$1:$A$4`;

  const headerRow = worksheet.getRow(HEADER_ROW_INDEX);
  headerRow.eachCell((cell) => {
    cell.protection = { locked: true };
  });
  headerRow.commit();

  const firstOptionColumn = worksheet.getColumn(
    FIRST_PREDEFINED_OPTION_COLUMN,
  ).letter;
  const lastOptionColumn = worksheet.getColumn(
    getPredefinedOptionColumnIndex(PREDEFINED_OPTION_COLUMN_COUNT - 1),
  ).letter;
  const answerOptionColumn = worksheet.getColumn(ANSWER_OPTION_COLUMN).letter;
  const predefinedComment =
    translations.answerOptions.predefinedComment.replace(/"/g, '""');

  worksheet.addConditionalFormatting({
    ref: `${firstOptionColumn}${START_ROW_INDEX}:${lastOptionColumn}${START_ROW_INDEX + MAX_TEMPLATE_ROWS - 1}`,
    rules: [
      {
        type: "expression",
        priority: 1,
        formulae: [
          `$${answerOptionColumn}${START_ROW_INDEX}="${predefinedComment}"`,
        ],
        style: {
          fill: {
            type: "pattern",
            pattern: "solid",
            bgColor: { argb: "FFFFFFFF" },
          },
        },
      },
      {
        type: "expression",
        priority: 2,
        formulae: [
          `$${answerOptionColumn}${START_ROW_INDEX}<>"${predefinedComment}"`,
        ],
        style: {
          fill: {
            type: "pattern",
            pattern: "solid",
            bgColor: { argb: "adadad" },
          },
        },
      },
    ],
  });

  for (
    let rowIndex = START_ROW_INDEX;
    rowIndex < START_ROW_INDEX + MAX_TEMPLATE_ROWS;
    rowIndex++
  ) {
    const row = worksheet.getRow(rowIndex);
    const itemNameCell = row.getCell(ITEM_NAME_COLUMN);
    const descriptionCell = row.getCell(DESCRIPTION_COLUMN);
    const answerOptionCell = row.getCell(ANSWER_OPTION_COLUMN);

    itemNameCell.protection = { locked: false };
    descriptionCell.protection = { locked: false };
    answerOptionCell.protection = { locked: false };

    itemNameCell.dataValidation = {
      type: "textLength",
      operator: "lessThanOrEqual",
      formulae: [100],
      allowBlank: true,
      showErrorMessage: true,
      errorStyle: "stop",
      errorTitle: "Invalid Entry",
      error: "Item Name cannot exceed 100 characters.",
    };

    descriptionCell.dataValidation = {
      type: "textLength",
      operator: "lessThanOrEqual",
      formulae: [150],
      allowBlank: true,
      showErrorMessage: true,
      errorStyle: "stop",
      errorTitle: "Invalid Entry",
      error: "Description cannot exceed 150 characters.",
    };

    answerOptionCell.dataValidation = {
      type: "list",
      allowBlank: false,
      formulae: [answerOptionFormula],
      showErrorMessage: true,
      errorStyle: "stop",
      errorTitle: "Invalid Entry",
      error: "Please select an answer option from the list.",
    };

    for (
      let optionIndex = 0;
      optionIndex < PREDEFINED_OPTION_COLUMN_COUNT;
      optionIndex++
    ) {
      const optionCell = row.getCell(
        getPredefinedOptionColumnIndex(optionIndex),
      );
      optionCell.protection = { locked: false };
      // Filled cells hide Excel gridlines, so preserve visible cell boundaries.
      optionCell.border = {
        top: { style: "thin", color: { argb: "FFBFBFBF" } },
        left: { style: "thin", color: { argb: "FFBFBFBF" } },
        bottom: { style: "thin", color: { argb: "FFBFBFBF" } },
        right: { style: "thin", color: { argb: "FFBFBFBF" } },
        ...optionCell.border,
      };
      optionCell.dataValidation = {
        type: "textLength",
        operator: "lessThanOrEqual",
        formulae: [MAX_PREDEFINED_OPTION_NAME_LENGTH],
        allowBlank: true,
        showErrorMessage: true,
        errorStyle: "stop",
        errorTitle: "Invalid Entry",
        error: "Option name cannot exceed 100 characters.",
      };
    }

    row.commit();
  }

  await worksheet.protect(PROTECTION_PASSWORD, {
    selectLockedCells: false,
    selectUnlockedCells: true,
  });
  await answerOptionsWorksheet.protect(PROTECTION_PASSWORD, {});
};

const getSortedPredefinedOptions = (
  item?: InspectionTemplateItem,
): { name: string; position?: number }[] =>
  [...(item?.options ?? [])].sort(
    (optionA, optionB) => (optionA.position ?? 0) - (optionB.position ?? 0),
  );

const readPredefinedOptionsFromRow = (row: Row) => {
  const options: { name: string; position: number }[] = [];

  for (
    let optionIndex = 0;
    optionIndex < PREDEFINED_OPTION_COLUMN_COUNT;
    optionIndex++
  ) {
    const optionName = getCellText(
      row.getCell(getPredefinedOptionColumnIndex(optionIndex)).value,
    );

    if (!optionName) {
      continue;
    }

    if (optionName.length > MAX_PREDEFINED_OPTION_NAME_LENGTH) {
      throwUploadError(INSPECTION_BULK_UPLOAD_ERROR.INVALID_TEMPLATE_FORMAT);
    }

    options.push({
      name: optionName,
      position: options.length + 1,
    });
  }

  return options;
};

const rowHasPredefinedOptionValues = (row: Row) =>
  Array.from({ length: PREDEFINED_OPTION_COLUMN_COUNT }, (_, optionIndex) =>
    getCellText(row.getCell(getPredefinedOptionColumnIndex(optionIndex)).value),
  ).some(Boolean);

const buildDefaultMeterInspectionItems = (
  translations: InspectionTemplateTranslations,
): InspectionItem[] => [
  {
    description: "",
    inspectionItemId: "01JFC51QCDFNTY1H7B00AXZHX6",
    isForcedRequired: true,
    isImmutable: true,
    isRequired: true,
    itemCode: "SERVICE_METER",
    itemSampleMedia: [],
    name: translations.meterItems.serviceMeter,
    resultType: "NUMERIC",
  },
  {
    description: "",
    inspectionItemId: "01JFC51QCDJXEV7G6XXWV3WBZ3",
    isForcedRequired: false,
    isImmutable: true,
    isRequired: false,
    itemCode: "ODOMETER",
    itemSampleMedia: [],
    name: translations.meterItems.odometer,
    resultType: "NUMERIC",
  },
];

export const buildInspectionBulkTemplateWorkbookBuffer = async (
  blankTemplateUrl: string,
  templateData: InspectionBulkTemplateData,
  translations: InspectionTemplateTranslations,
) => {
  const fileResponse = await fetch(blankTemplateUrl);
  if (!fileResponse.ok) {
    throw new Error("Failed to fetch blank inspection form template.");
  }

  const arrayBuffer = await fileResponse.arrayBuffer();
  const workbook = new Workbook();
  await workbook.xlsx.load(arrayBuffer);

  const worksheet = getTargetWorksheet(workbook);
  if (!worksheet) {
    throw new Error("Inspection form worksheet was not found.");
  }

  const optionHeaders = getPredefinedOptionColumnHeaders(translations);

  // Set headers from i18n translations — same as machine export
  const headerRow = worksheet.getRow(HEADER_ROW_INDEX);
  headerRow.values = [
    translations.headers.itemName,
    translations.headers.description,
    translations.headers.answerOption,
    ...optionHeaders,
  ];
  headerRow.eachCell((cell) => {
    cell.font = { bold: true };
  });
  headerRow.commit();

  const { answerOptionByResultType } = buildAnswerOptionMaps(translations);
  const templateItems = getTemplateItems(
    templateData,
    answerOptionByResultType,
  );

  await applyWorksheetRules(workbook, worksheet, translations);

  for (let index = 0; index < MAX_TEMPLATE_ROWS; index++) {
    const row = worksheet.getRow(START_ROW_INDEX + index);
    const item = templateItems[index];
    const sortedOptions = getSortedPredefinedOptions(item);

    row.getCell(ITEM_NAME_COLUMN).value = item?.name ?? "";
    row.getCell(DESCRIPTION_COLUMN).value = item?.description ?? "";
    row.getCell(ANSWER_OPTION_COLUMN).value = item?.resultType
      ? answerOptionByResultType[item.resultType]
      : "";

    for (
      let optionIndex = 0;
      optionIndex < PREDEFINED_OPTION_COLUMN_COUNT;
      optionIndex++
    ) {
      row.getCell(getPredefinedOptionColumnIndex(optionIndex)).value =
        sortedOptions[optionIndex]?.name ?? "";
    }

    row.commit();
  }

  return workbook.xlsx.writeBuffer();
};

export const buildInspectionFormDataFromBulkUploadFile = async (
  file: File,
  translations: InspectionTemplateTranslations,
): Promise<InspectionsType> => {
  const workbook = new Workbook();
  const arrayBuffer = await file.arrayBuffer();
  await workbook.xlsx.load(arrayBuffer);
  const worksheet = getTargetWorksheet(workbook);

  if (!worksheet) {
    throwUploadError(INSPECTION_BULK_UPLOAD_ERROR.INVALID_TEMPLATE_FORMAT);
  }

  validateUploadHeaders(worksheet!, translations);

  if (worksheet!.rowCount <= HEADER_ROW_INDEX) {
    throwUploadError(INSPECTION_BULK_UPLOAD_ERROR.NO_INFORMATION);
  }

  const { resultTypeByAnswerOption } = buildAnswerOptionMaps(translations);

  const inspectionItems: InspectionItem[] =
    buildDefaultMeterInspectionItems(translations);

  for (
    let index = 0;
    index < MAX_TEMPLATE_ROWS && START_ROW_INDEX + index <= worksheet!.rowCount;
    index++
  ) {
    const row = worksheet!.getRow(START_ROW_INDEX + index);
    const name = getCellText(row.getCell(ITEM_NAME_COLUMN).value);
    const description = getCellText(row.getCell(DESCRIPTION_COLUMN).value);
    const answerOption = getCellText(row.getCell(ANSWER_OPTION_COLUMN).value);
    const predefinedOptions = readPredefinedOptionsFromRow(row);

    if (
      !name &&
      !description &&
      !answerOption &&
      !rowHasPredefinedOptionValues(row)
    ) {
      continue;
    }

    const resultType =
      resultTypeByAnswerOption[answerOption.toLowerCase()] ?? "OK_OR_ANOMARY";

    if (name.length > 100) {
      throwUploadError(INSPECTION_BULK_UPLOAD_ERROR.INVALID_TEMPLATE_FORMAT);
    }
    if (description.length > 150) {
      throwUploadError(INSPECTION_BULK_UPLOAD_ERROR.INVALID_TEMPLATE_FORMAT);
    }
    // Throw only if a non-empty answerOption was provided but unrecognized
    if (answerOption && !resultTypeByAnswerOption[answerOption.toLowerCase()]) {
      throwUploadError(INSPECTION_BULK_UPLOAD_ERROR.INVALID_TEMPLATE_FORMAT);
    }

    // A "Predefined comment selection" row with no options is allowed through —
    // it lands in the form with options: [] and the existing per-item validation
    // (Card.tsx) keeps Publish/Save Draft disabled until the user fills it in.

    inspectionItems.push({
      inspectionItemId: `bulk-upload-${inspectionItems.length - 1}`,
      name,
      previousName: name,
      description,
      customInspectionItemMedias: [],
      inspectionItemMedias: [],
      isNew: true,
      isOpen: false,
      isForcedRequired: false,
      isImmutable: false,
      isRequired: false,
      itemCode: null,
      resultType,
      ...(resultType === "FREE_TEXT" ? { options: predefinedOptions } : {}),
    });
  }

  if (inspectionItems.length <= 2) {
    throwUploadError(INSPECTION_BULK_UPLOAD_ERROR.NO_VALID_DATA);
  }

  return {
    name: "",
    inspectionFormId: "01JFC51QCD7THZWG52BACM8Q53",
    isBulkUpload: true,
    customInspectionItems: [],
    inspectionItems: limitInspectionFormItems(inspectionItems),
  } as InspectionsType & { isBulkUpload: boolean };
};

export const downloadInspectionBulkTemplateWorkbook = (
  buffer: BlobPart,
  fileName: string,
) => {
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
};

export const buildInspectionBulkTemplateFileName = (templateName?: string) => {
  const date = new Date();
  const formattedDate = `${date.getDate().toString().padStart(2, "0")}-${(
    date.getMonth() + 1
  )
    .toString()
    .padStart(2, "0")}-${date.getFullYear()}`;
  const formattedTime = `(${date.getHours().toString().padStart(2, "0")}-${date
    .getMinutes()
    .toString()
    .padStart(2, "0")}-${date.getSeconds().toString().padStart(2, "0")})`;
  const safeTemplateName = (templateName || "inspection-form-template")
    .replace(/[\\/:*?"<>|]/g, "_")
    .trim();

  return `${
    safeTemplateName || "inspection-form-template"
  }-${formattedDate}-${formattedTime}.xlsx`;
};
